import { Link } from "react-router-dom";
import { useState } from "react";
import { Check, Building2 } from "lucide-react";
import RevealNav from "@/components/RevealNav";
import SiteFooter from "@/components/SiteFooter";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";

export default function Pricing() {
  const [installment, setInstallment] = useState(false);

  const setupToday = installment ? 150 : 899;

  return (
    <div>
      <RevealNav />
      <section className="ds-container pt-32 pb-24">
        <div className="max-w-2xl mb-12">
          <p className="overline mb-4">pricing</p>
          <h1 className="text-5xl lg:text-6xl mb-4">
            <span className="font-script text-primary text-5xl lg:text-6xl font-normal block leading-[1.05] mb-2">ai is here to save you money and make more of it</span>
            Built to scale.
          </h1>
          <p className="text-lg text-muted-foreground">No predatory contracts or hidden percentages. Get started with flexible options that keep revenue in your operation.</p>
        </div>

        {/* Setup-fee installment toggle */}
        <Card className="p-5 mb-8 flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="text-sm font-bold mb-1">One-time setup: $899</div>
            <div className="text-xs text-muted-foreground">Or spread it over 6 months at $150/mo</div>
          </div>
          <div className="flex items-center gap-3">
            <span className={`text-sm ${!installment ? "font-bold" : "text-muted-foreground"}`}>Pay $899 today</span>
            <Switch checked={installment} onCheckedChange={setInstallment} data-testid="pricing-installment-toggle"/>
            <span className={`text-sm ${installment ? "font-bold" : "text-muted-foreground"}`}>$150/mo × 6</span>
          </div>
        </Card>

        {/* TIERS */}
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          <Card className="tracing-beam p-10 relative bg-card" data-testid="pricing-tier-pro">
            <div className="absolute top-4 right-4 overline text-primary">recommended</div>
            <p className="overline mb-3">comprehensive solution</p>
            <h3 className="text-2xl mb-1">Establishment</h3>
            <div className="text-6xl font-extrabold mb-1">$179<span className="text-xl text-muted-foreground font-normal">/mo</span></div>
            <p className="text-sm text-muted-foreground mb-6">+ ${setupToday}{installment ? "/mo setup × 6" : " one-time setup"} · + platform fees</p>
            <ul className="space-y-3 mb-8 text-sm">
              {["Full storefront or booking page on your domain",
                "Instant Stripe Connect payouts",
                "Active catalog, menu, or service manager with availability toggles",
                "Live operations control room",
                "Real-time AI phone receptionist (DeepSeek)",
                "Direct PrintNode thermal printing, POS push, or team workflow handoff"].map(f => (
                <li key={f} className="flex items-start gap-2"><Check className="w-4 h-4 text-primary mt-0.5"/>{f}</li>
              ))}
            </ul>
            <Link to="/subscribe"><Button className="w-full h-14 font-script tracking-widest text-base" data-testid="pricing-cta-pro">Deploy Custom Storefront</Button></Link>
          </Card>

          <Card className="p-10 bg-card" data-testid="pricing-tier-enterprise">
            <p className="overline mb-3 flex items-center gap-1.5"><Building2 className="w-3 h-3"/>2+ locations</p>
            <h3 className="text-2xl mb-1">Enterprise</h3>
            <div className="text-6xl font-extrabold mb-1">$99<span className="text-xl text-muted-foreground font-normal">/mo /per location</span></div>
            <p className="text-sm text-muted-foreground mb-6">+ ${setupToday}{installment ? "/mo setup × 6" : " one-time setup"} + platform fees</p>
            <ul className="space-y-3 mb-8 text-sm">
              {["Everything in Pro, every location",
                "Add / edit unlimited locations from one dashboard",
                "Per-location address, phone, web link, POS config, printer",
                "Centralized catalog, menu, or service sync across locations",
                "Dedicated onboarding manager",
                "Priority support"].map(f => (
                <li key={f} className="flex items-start gap-2"><Check className="w-4 h-4 text-primary mt-0.5"/>{f}</li>
              ))}
            </ul>
            <Link to="/subscribe"><Button className="w-full h-14 font-script tracking-widest text-base" data-testid="pricing-cta-enterprise">Order ordrs</Button></Link>
          </Card>
        </div>

        {/* PLATFORM FEES — YOUR CHOICE */}
        <Card className="p-10">
          <p className="overline mb-3">platform fees — your choice</p>
          <h3 className="text-3xl mb-2">Keep your overhead at <span className="font-script text-primary text-4xl">net-zero.</span></h3>
          <p className="text-muted-foreground mb-8 max-w-2xl">Pick the model that fits your operating model. Toggle anytime from your dashboard.</p>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="border-2 border-border rounded-lg p-6 hover:border-primary/50 transition-colors">
              <p className="overline mb-2">competitive</p>
              <div className="text-4xl font-extrabold mb-1">1%</div>
              <div className="text-sm text-muted-foreground mb-3">percentage fee · charged to your business</div>
              <p className="text-sm">You absorb 1% on every transaction. Customers see the listed price, no surprises. Best for a premium-feel checkout.</p>
            </div>
            <div className="border-2 border-primary rounded-lg p-6 bg-primary/5">
              <p className="overline mb-2 text-primary">most popular</p>
              <div className="text-4xl font-extrabold mb-1">$1.50</div>
              <div className="text-sm text-muted-foreground mb-3">flat fee · passed to customer</div>
              <p className="text-sm">Customers see a $1.50 convenience fee at checkout. Your overhead stays net-zero and the listed sale amount stays yours.</p>
            </div>
          </div>
        </Card>
      </section>
      <SiteFooter />
    </div>
  );
}
